import moment from "moment"

import BigNumber from "bignumber.js"
import {
  LadenStatus,
  PrevetSpeedGraphZoneType,
  ballastColor,
  ballastLegLineColor,
  ballastMarkAreaColor,
  calcMarkLineOffset,
  createAllZones,
  createLegPieces,
  createZonesOfConcern,
  drawLegLines,
  findIndexOfSinglePointZones,
  getTitleText,
  ladenColor,
  ladenLegLineColor,
  ladenMarkAreaColor,
} from "../getSpeedChartOptionsHelpers"
import { VesselLeg } from "../hooks/usePrevetAPI"
import { average as findAverage } from "simple-statistics"

export function getSpeedChartOptions({
  vesselName,
  speedDataRaw,
  ladenStatus,
  months,
  showAllZones,
  userVesselSpeed,
  useForMinTreshold,
  globalMin,
  globalMax,
  descSpeed,
  avgSpeed,
  showSeaMargin,
  hideTitle,
  manualSeaMarginPct,
}: {
  vesselName: string
  speedDataRaw: VesselLeg[]
  ladenStatus: LadenStatus
  months: number
  showAllZones: boolean
  userVesselSpeed: string | undefined
  useForMinTreshold?: PrevetSpeedGraphZoneType
  globalMin: number
  globalMax: number
  descSpeed: number
  avgSpeed: number
  showSeaMargin: boolean
  hideTitle?: boolean
  manualSeaMarginPct?: number | null
}) {
  const speedData = speedDataRaw.map((data) => {
    // Chart does not like it when first property is "laden"
    return {
      pos_date: data.pos_date,
      sog: data.sog,
      laden: data.laden,
      zone_code: data.zone_code,
      zone_name: data.zone_name,
      zone_alias: data.zone_alias,
      zone_group: data.zone_group,
      prev_port: data.prev_port,
      next_port: data.next_port,
    }
  })

  /* dummy data for seamargin */
  const seaMarginDummyData = speedDataRaw.map((d) => {
    return {
      pos_date: d.pos_date,
      sea_margin: d.margin ? d.margin * 100 : null,
    }
  })

  const averageSpeed =
    speedData.length > 0 && findAverage(speedData.map((item) => item.sog))

  const titleText = getTitleText({
    speedData: speedDataRaw,
    months,
    ladenStatus,
    descSpeed,
    avgSpeed,
    showSeaMargin,
  })

  const zonesOfConcern = createZonesOfConcern({
    speedData,
    isLaden: ladenStatus === "laden",
    useForMinTreshold: useForMinTreshold as PrevetSpeedGraphZoneType,
  })
  // Hacky code to draw markArea in shadow xAxis around a single data point
  const singlePointZones = zonesOfConcern.filter((zone) => {
    return zone[0].xAxis === zone[1].xAxis
  })

  const indexOfSinglePointZones = findIndexOfSinglePointZones({
    speedData,
    singlePointZones,
  })

  /**
   * Variables to help with custom formatting of x-axis lables
   * prevMonth keeps track of the month at the current index - 1
   */
  let prevMonth = ""

  const options = {
    color: [ladenStatus === "laden" ? ladenColor : ballastColor],
    dataset: [
      {
        source: speedData,
      },
      {
        source: seaMarginDummyData,
      },
    ],
    title: hideTitle
      ? { show: false }
      : {
          show: true,
          left: speedData.length < 1 ? "center" : "left",
          top: speedData.length < 1 ? "40%" : "auto",
          text: titleText,
          textStyle: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 20,
            width: speedData.length < 1 ? 220 : 600,
            fontFamily: "Source Sans Pro",
            overflow: "break",
            rich: {
              sm: { padding: [0, 4, 0, 6] },
              danger: { color: "red" },
              success: { color: "green" },
              warning: { color: "orange" },
              value: { padding: [0, 24, 0, 6] },
              red: {
                color: "red",
                padding: [0, 0, 0, 16],
              },
              green: {
                color: "green",
                padding: [0, 0, 0, 16],
              },
              gray: {
                color: "#464646",
                padding: [0, 0, 0, 16],
              },
              pill: {
                backgroundColor: "#3050a2",
                marginLeft: 8,
                color: "#fff",
                padding: [3, 8],
                borderRadius: 12,
                fontSize: 10,
                fontWeight: "normal",
                lineHeight: 16,
              },
            },
          },
        },
    grid: [
      {
        top: hideTitle ? 40 : 90,
        height: hideTitle ? 260 : 220,
        right: 50,
        left: 40,
      },
      {
        left: 40,
        right: 50,
        top: hideTitle ? 190 : 200,
        height: 110,
      },
    ],
    toolbox: {
      feature: {
        brush: {
          show: false,
        },
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: (params: any) => {
        const dp = params[0].data as VesselLeg
        const seaMarginParam = params.find(
          (p: any) => p.seriesName === "Sea Margin"
        )
        const seaMarginValue =
          seaMarginParam?.data?.sea_margin ??
          seaMarginParam?.value?.sea_margin ??
          null
        return `
          <div>${moment(dp.pos_date).format("DD-MM-YYYY")}<div>
          <div>Zone: ${dp.zone_name}</div>
          <div>${dp.prev_port} - ${dp.next_port ? dp.next_port : "TBD"}</div>
          <div style="display:flex; justify-content:space-between;">
            <div style="display:flex; align-items:center;">
              <div style="top:-1px; position: relative; width:10px; height:10px; background-color:${
                ladenStatus === "laden" ? ladenColor : ballastColor
              }; border-radius:100%; margin-right:4px;"></div>
              <div>${dp.laden ? "LADEN" : "BALLAST"}</div>
            </div>
            <div>${dp.sog}</div>
          </div>
           ${
             seaMarginValue != null && showSeaMargin
               ? `
               <div style="display:flex; justify-content:space-between;">
               <div>Sea Margin:</div>
               <div style = "color:${
                 seaMarginValue > 10
                   ? "red"
                   : seaMarginValue > 6
                   ? "orange"
                   : "green"
               }">${Number(seaMarginValue).toFixed(1)}%</div>
               </div>${
                 manualSeaMarginPct != null
                   ? `<div style="display:flex; justify-content:space-between;">
                      <div>Manual Sea Margin:</div>
                      <div style="color:${
                        manualSeaMarginPct > 10
                          ? "red"
                          : manualSeaMarginPct > 6
                          ? "orange"
                          : "green"
                      }">${manualSeaMarginPct.toFixed(1)}%</div>
                      </div>`
                   : ""
               }`
               : ""
           }
        `
      },
    },
    /* brush select for data removal */
    brush: {
      geoIndex: 0,
      brushLink: "lineX",
      toolbox: ["lineX"],
      xAxisIndex: "all",
      throttleType: "debounce",
      throttleDelay: 300,
      transformable: false,
    },
    visualMap: {
      show: false,
      dimension: 0,
      pieces: createLegPieces({ speedData, isLaden: ladenStatus === "laden" }),
    },
    xAxis: [
      {
        type: "category",
        gridIndex: 0,
        axisPointer: {
          type: "shadow",
          label: {
            show: true,
            formatter: function (params: any) {
              return moment(new Date(params.value)).format("DD-MM-YYYY")
            },
          },
        },
        axisLabel: {
          formatter: (params: any, index: number) => {
            const currMonth = moment(params).format("MMM")
            if (index === speedData.length - 1) {
              // Check if we are on the last iteration of the dataset, If so, we always want to
              // return the current month to end the x-axis. However if there is not enough space,
              // chart will not render the last month to cap the x-axis.
              return currMonth
            }
            if (currMonth === prevMonth) {
              return ""
            }
            prevMonth = currMonth
            return currMonth
          },
          margin: 6,
          hideOverlap: true,
        },
      },
      {
        type: "category",
        gridIndex: 1,
        data: speedData.map((d) => d.pos_date),
        axisLabel: { show: false },
        axisTick: { show: false },
      },
      !showAllZones && {
        // Creates a "shadow" xAxis with a 2x grid granularity. This grid helps to draw the markArea
        // around a single data point. Simplified example: https://codepen.io/isachenx/pen/mdojBmQ
        type: "category",
        xAxisIndex: 2,
        yIndexAxis: 0,
        data: Array.from({ length: speedData.length * 2 }),
        axisPointer: {
          show: false,
        },
        show: false,
      },
    ],
    yAxis: [
      {
        type: "value",
        min: globalMin,
        max: globalMax,
        gridIndex: 0,
        minInterval: 1,
        axisLabel: {
          formatter: "{value} Kt",
          margin: 2,
        },
        splitLine: {
          show: false,
        },
      },
      {
        type: "value",
        gridIndex: 1,
        position: "right",
        min: 0,
        max: manualSeaMarginPct != null && manualSeaMarginPct > 15 ? Math.ceil(manualSeaMarginPct / 5) * 5 : 15,
        minInterval: 5,
        axisLabel: {
          formatter: "{value}%",
          margin: 2,
        },
        splitLine: {
          show: false,
        },
      },
    ],
    dataZoom: [
      {
        backgroundColor: ladenStatus === "laden" ? "#b2b6c3" : "#ffc9bb",
        fillerColor: ladenStatus === "laden" ? ladenColor : ballastColor,
        bottom: 28,
        textStyle: {
          width: 55,
          color: "#333333",
          fontSize: 10,
        },
        handleSize: "200%",
        moveHandleStyle: {
          opacity: 0,
        },
        type: "slider",
        show: true,
        xAxisIndex: [0, 1],
        height: 10,
        selectedDataBackground: {
          lineStyle: {
            color: "rgba(1,1,1,0)",
          },
          areaStyle: {
            color: "rgba(1,1,1,0)",
          },
        },
        dataBackground: {
          lineStyle: {
            color: "rgba(1,1,1,0)",
          },
          areaStyle: {
            color: "rgba(1,1,1,0)",
          },
        },
      },
    ],
    series: [
      /** Items in series array need to maintain index order for Performance Analysis component updates to the chart instance */
      {
        name: ladenStatus,
        type: "line",
        encode: {
          x: "pos_date",
          y: "sog",
        },
        areaStyle: {
          opacity: 0.35,
          /* color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color:
                  ladenStatus === "ballast"
                    ? "rgba(255, 99, 71, 0.65)"
                    : "rgba(0, 20, 70, 0.65)",
              },
              {
                offset: 0.45,
                color:
                  ladenStatus === "ballast"
                    ? "rgba(255, 99, 71, 0.35)"
                    : "rgba(0, 20, 70, 0.35)",
              },
              {
                offset: 1,
                color: "rgba(0,0,0,0)",
              },
            ],
          }, */
        },
        symbolSize: 8,
        markLine: {
          symbol: ["none", "none"],
          label: { show: false },
          lineStyle: {
            color:
              ladenStatus === "laden" ? ballastLegLineColor : ladenLegLineColor,
            type: "dotted",
            width: 2,
          },
          data: drawLegLines(speedData),
        },
        markArea: {
          label: {
            fontSize: 8,
          },
          data: showAllZones ? createAllZones({ speedData }) : zonesOfConcern,
        },
      },
      {
        name: `${ladenStatus} Avg`,
        type: "line",
        showSymbol: false,
        color: "transparent",
        tooltip: {
          show: false,
        },
        markLine: {
          precision: 3,
          symbolSize: 7,
          symbol: ["none"],
          lineStyle: {
            color: ladenStatus === "laden" ? ladenColor : ballastColor,
            type: "dashed",
            opacity: 0.75,
          },
          data: [
            {
              type: "average",
              name: "Avg",
              label: { show: false },
              emphasis: {
                label: {
                  show: true,
                  offset: [0, 0],
                  fontSize: 10,
                  fontWeight: "bold",
                  formatter: (params: any) => {
                    const value = params.value
                    const rounded = BigNumber(value)
                      .decimalPlaces(1, BigNumber.ROUND_HALF_CEIL)
                      .toString()
                    return `${rounded} Avg`
                  },
                },
              },
            },
          ],
        },
      },
      /* Create markArea in the shadow xAxis for single point zones.
      /* Only necessary for the SpeedGraph, but not in modal which shows all zones */
      !showAllZones && {
        type: "line",
        xAxisIndex: 2,
        yIndexAxis: 0,
        gridIndex: 0,
        markArea: {
          itemStyle: {
            opacity: 0.2,
            color:
              ladenStatus === "laden"
                ? ladenMarkAreaColor
                : ballastMarkAreaColor,
          },
          data: indexOfSinglePointZones.map((idx) => {
            return [{ xAxis: idx * 2 }, { xAxis: idx * 2 + 1 }]
          }),
        },
      },
      {
        name: `USR VESSEL`, // Put this on its own markLine because of symbol: none
        type: "line",
        markLine: {
          symbolSize: 7,
          symbol: ["none"],
          lineStyle: {
            color: ladenStatus === "laden" ? ladenColor : ballastColor,
            type: "dashed",
            opacity: 0.75,
          },
          data: [
            {
              name: "User Vsl",
              yAxis: Number(userVesselSpeed),
              label: {
                fontSize: 10,
                offset: averageSpeed
                  ? calcMarkLineOffset({
                      label: Number(userVesselSpeed),
                      compared: averageSpeed,
                    })
                  : [0, 0],
                formatter: (params: any) => {
                  return `${BigNumber(params.data.yAxis).toFormat(1)} User Vsl`
                },
              },
            },
          ],
        },
      },
      /* SeaMargin data */
      showSeaMargin && {
        name: "Sea Margin",
        type: "line",
        silent: true,
        datasetIndex: 1,
        xAxisIndex: 1,
        yAxisIndex: 1,
        encode: {
          x: "pos_date",
          y: "sea_margin",
        },
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2,
          color: "rgba(70, 100, 255, 0)", // hidden normally
        },
        areaStyle: {
          opacity: 1,
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(70, 100, 255, 0.4)" },
              { offset: 1, color: "rgba(0, 0, 0, 0)" },
            ],
          },
        },
        emphasis: {
          focus: "series",
          lineStyle: {
            color: "rgba(70, 100, 255, 1)", // solid on hover
            width: 2,
            shadowBlur: 6,
            shadowColor: "rgba(70,100,255,0.6)",
          },
        },
        z: 2,
      },
    ],
  }
  return options
}
